import { forwardRef, useMemo } from 'react';

/** A PDF point is 1/72 inch. */
const POINTS_PER_INCH = 72;
/** Ticks every eighth of an inch, the way a word processor's ruler is cut. */
const TICKS_PER_INCH = 8;

/** How thick the strip is, both axes, so the corner between them is square. */
export const RULER_SIZE = 22;

/**
 * A ruler beside the page — above it, or down its left edge.
 *
 * Shaped like the one in a word processor rather than a drawing app: the strip
 * runs the whole length of the scrolling area, and the SHEET is the light band
 * inside it, with the margins picked out darker and an inch count along it.
 * That is what makes it readable at a glance — the numbers say how far into the
 * paper you are, and the band says where the paper stops.
 *
 * It is drawn in page units, not screen ones: an inch is an inch of paper
 * whatever the zoom.
 *
 * @param {{ length: number, scale: number, margin?: number, span: number,
 *   origin: number, orientation?: 'horizontal'|'vertical' }} props
 *   `length` is the page's own width or height in points, for this axis.
 *   `origin` is where the page starts inside the strip, in laid-out pixels.
 *   The strip itself is `position: sticky`: it fills its column, so it is as
 *   long as the CONTENT, and the compositor holds it to its edge without a line
 *   of JavaScript — which is what keeps it still under a fast wheel.
 */
const Ruler = forwardRef(function Ruler({
    length, scale, margin = 0, origin = 0, orientation = 'horizontal',
}, ref) {
    const vertical = orientation === 'vertical';
    const size = Math.max(0, Math.round(length * scale));

    const marks = useMemo(() => {
        if (!size) return [];
        const step = (POINTS_PER_INCH / TICKS_PER_INCH) * scale;
        // Below about 4px a tick is a smudge, not a mark.
        if (step < 4) return [];
        const out = [];
        for (let i = 0, at = 0; at <= size + 0.5; i += 1, at = i * step) {
            const inch = i / TICKS_PER_INCH;
            out.push({
                at,
                // The count starts again at every inch, and 0 stays blank: the
                // corner of the sheet needs no number to explain it.
                label: Number.isInteger(inch) && inch > 0 ? String(inch) : null,
                major: Number.isInteger(inch),
                half: i % (TICKS_PER_INCH / 2) === 0,
            });
        }
        return out;
    }, [size, scale]);

    if (!size) return null;

    const inset = Math.round(margin * scale);
    const thickness = RULER_SIZE;

    return (
        <div
            ref={ref}
            aria-hidden="true"
            className={`pointer-events-none z-30 flex-none select-none border-[#E2DCD6] bg-[#F1EDE9] dark:border-zinc-700 dark:bg-zinc-900 ${
                vertical ? 'sticky left-0 self-stretch' : 'sticky top-0 w-full'
            }`}
            style={vertical
                ? { width: thickness, borderRightWidth: 1 }
                : { height: thickness, borderBottomWidth: 1 }}
        >
            {/* The sheet: lighter than the rest of the strip, with the margins
                shaded back so the band between them is the text column. */}
            <span
                className="absolute bg-white dark:bg-zinc-800"
                style={vertical
                    ? { left: 0, right: 0, top: origin, height: size }
                    : { top: 0, bottom: 0, left: origin, width: size }}
            />
            {inset > 0 ? (
                <>
                    <span
                        className="absolute bg-[#D8D2CC] dark:bg-zinc-700"
                        style={vertical
                            ? { left: 0, right: 0, top: origin, height: inset }
                            : { top: 0, bottom: 0, left: origin, width: inset }}
                    />
                    <span
                        className="absolute bg-[#D8D2CC] dark:bg-zinc-700"
                        style={vertical
                            ? { left: 0, right: 0, top: origin + size - inset, height: inset }
                            : { top: 0, bottom: 0, left: origin + size - inset, width: inset }}
                    />
                </>
            ) : null}

            {marks.map((mark) => {
                const at = Math.round(origin + mark.at);
                if (mark.label) {
                    return (
                        <span
                            key={mark.at}
                            className="absolute text-[9px] leading-none text-[#6B635C] dark:text-zinc-400"
                            style={vertical
                                ? { top: at - 4, left: 0, width: thickness, textAlign: 'center' }
                                : { left: at - 4, top: 6, width: 8, textAlign: 'center' }}
                        >
                            {mark.label}
                        </span>
                    );
                }
                // Halves reach further in than eighths, and neither touches the
                // numbers — the tick row reads as a scale, not as noise.
                const len = mark.half ? 6 : 3;
                return (
                    <span
                        key={mark.at}
                        className="absolute bg-[#A8A099] dark:bg-zinc-500"
                        style={vertical
                            ? { top: at, right: 2, height: 1, width: len }
                            : { left: at, bottom: 2, width: 1, height: len }}
                    />
                );
            })}
        </div>
    );
});

export default Ruler;
